import { HybridTooltip, Icon } from '@advanza/ui'
import { DebtorBucket, DebtorStatusBuckets } from '@trustoo/types/api/controllers/Invoices'
import clsx from 'clsx'
import { formatEuro, formatPercent1, percentageOf } from 'components/billing/debtorNew/format'
import { ReactNode } from 'react'
import { Link } from 'react-router-dom'

// Re-exported so the existing debtor imports keep working now that it is shared.
export { Segmented } from 'components/ui/Segmented'

/**
 * Hover/tap explanation for the new-vocabulary terms on this page (status buckets, funnel steps,
 * badges). Renders the trigger with a subtle dotted underline so users can discover it.
 */
export const HelpTip = ({ help, children }: { help: string; children: ReactNode }) => (
    <HybridTooltip childrenContent={<div className="max-w-xs text-sm leading-relaxed">{help}</div>}>
        <span className="cursor-help border-b border-dotted border-neutral-300">{children}</span>
    </HybridTooltip>
)

/* -------------------------------------------------------------------------- */
/*  Layout primitives                                                         */
/* -------------------------------------------------------------------------- */

interface CardProps {
    className?: string
    children: ReactNode
}

export const Card = ({ className, children }: CardProps) => (
    <div className={clsx('rounded-xl border border-neutral-200 bg-white', className)}>
        {children}
    </div>
)

/* -------------------------------------------------------------------------- */
/*  Status taxonomy (shared colors + labels)                                  */
/* -------------------------------------------------------------------------- */

/**
 * Buckets where money actually came in. `paidStale` = payment received but the status was never
 * re-synced.
 */
export const PAID_STATUS_KEYS: (keyof DebtorStatusBuckets)[] = ['paid', 'fulfilled', 'paidStale']

/**
 * Buckets closed without payment: written off, or no receivable to begin with (credit invoices
 * and rows <= €2).
 */
export const CLOSED_STATUS_KEYS: (keyof DebtorStatusBuckets)[] = ['lost', 'creditOrZero']

/** All settled / non-receivable buckets. Order = display order. */
export const SETTLED_STATUS_KEYS: (keyof DebtorStatusBuckets)[] = [
    ...PAID_STATUS_KEYS,
    ...CLOSED_STATUS_KEYS,
]

/** Buckets that make up the open (still collectable) amount; sums to totals.openAmount. */
export const OPEN_STATUS_KEYS: (keyof DebtorStatusBuckets)[] = [
    'debtCollection',
    'sentToCollector',
    'retryFailed',
    'retryPending',
    'dunning',
    'chargedBackFailed',
    'noStatus',
    'otherOpen',
]

export const STATUS_ORDER: (keyof DebtorStatusBuckets)[] = [
    ...SETTLED_STATUS_KEYS,
    ...OPEN_STATUS_KEYS,
]

export const STATUS_META: Record<
    keyof DebtorStatusBuckets,
    { label: string; bar: string; help: string }
> = {
    paid: {
        label: 'Paid (incl. refunded)',
        bar: 'bg-green-500',
        help: 'Payment received and confirmed. Includes the handful of refunded invoices (~14/year).',
    },
    fulfilled: {
        label: 'Fulfilled',
        bar: 'bg-green-300',
        help: 'Settled without a regular payment, e.g. compensated or manually marked as done.',
    },
    paidStale: {
        label: 'Paid (stale status)',
        bar: 'bg-green-200',
        help: 'A payment was received (pay date is set) but the invoice status was never updated afterwards — e.g. an abandoned duplicate payment link expired later and overwrote the status. Counted as settled. Invoices whose payment was charged back and not recovered are NOT in this bucket; they count as open under the dunning stages.',
    },
    lost: {
        label: 'Written off (lost)',
        bar: 'bg-neutral-400',
        help: 'Marked as lost — we no longer expect to collect this invoice.',
    },
    creditOrZero: {
        label: 'Credits & ≤ €2',
        bar: 'bg-neutral-200',
        help: 'Credit invoices (negative) and amounts of €2 or less. These carry no receivable, so they are excluded from the open amount.',
    },
    debtCollection: {
        label: 'Debt collection',
        bar: 'bg-orange-300',
        help: 'Handed over to the external debt collector.',
    },
    sentToCollector: {
        label: 'In dunning (at Payt)',
        bar: 'bg-orange-300',
        help: 'Sent to the payment collector (Payt) for the reminder process. Still open and collectable, but not in formal debt collection yet.',
    },
    retryFailed: {
        label: 'Retry failed',
        bar: 'bg-red-600',
        help: 'The second direct-debit attempt also bounced. Next stop is usually debt collection.',
    },
    retryPending: {
        label: 'Retry pending',
        bar: 'bg-amber-300',
        help: 'A second direct-debit attempt has been created and is awaiting the outcome.',
    },
    dunning: {
        label: 'Reminder sent (1st failure)',
        bar: 'bg-amber-500',
        help: 'The first payment attempt failed or was charged back; our own reminder process is running but no retry has been made yet and it has not been sent to Payt.',
    },
    chargedBackFailed: {
        label: 'Charged back (no follow-up)',
        bar: 'bg-red-400',
        help: 'Charged back or failed, but the automatic follow-up was cancelled or never created. Rare — usually every charge-back enters dunning automatically.',
    },
    noStatus: {
        label: 'No status',
        bar: 'bg-neutral-300',
        help: 'No payment transaction on record, e.g. the direct debit has not been created yet.',
    },
    otherOpen: {
        label: 'Other open',
        bar: 'bg-primary-300',
        help: 'Everything else that is still open: payment plans, SEPA in creation, expired payment links, etc.',
    },
}

interface StatusBarProps {
    bucket: DebtorBucket
    /** Which buckets to render; defaults to all. Pass OPEN_STATUS_KEYS for the open-only bar. */
    keys?: (keyof DebtorStatusBuckets)[]
}

/**
 * Horizontal stacked bar of the status distribution (by amount). Negative bucket amounts (credit
 * invoices) are clamped to 0 and the denominator only counts positive amounts, so segment widths
 * always add up to 100%.
 */
export const StatusBar = ({ bucket, keys = STATUS_ORDER }: StatusBarProps) => {
    const total = keys.reduce((sum, key) => sum + Math.max(bucket.statuses[key].amount, 0), 0)
    return (
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-neutral-100">
            {keys.map((key) => {
                const amount = bucket.statuses[key].amount
                const pct = total > 0 ? (Math.max(amount, 0) / total) * 100 : 0
                if (pct <= 0) {
                    return null
                }
                return (
                    <div
                        key={key}
                        className={STATUS_META[key].bar}
                        style={{ width: `${pct}%` }}
                        title={`${STATUS_META[key].label}: ${formatEuro(amount)}`}
                    />
                )
            })}
        </div>
    )
}

const LegendRows = ({
    bucket,
    keys,
    total,
    invoicesMonth,
}: {
    bucket: DebtorBucket
    keys: (keyof DebtorStatusBuckets)[]
    total: number
    /** About-month (YYYY-MM) used to deep-link rows to the invoices tab; null disables links. */
    invoicesMonth?: string | null
}) => (
    <>
        {keys.map((key) => {
            const value = bucket.statuses[key]
            return (
                <tr key={key}>
                    <td className="py-1.5 pr-2">
                        <span className="flex items-center gap-2">
                            <span
                                className={clsx(
                                    'inline-block h-2.5 w-2.5 rounded-sm',
                                    STATUS_META[key].bar,
                                )}
                            />
                            <HelpTip help={STATUS_META[key].help}>
                                <span className="text-neutral-700">{STATUS_META[key].label}</span>
                            </HelpTip>
                            {invoicesMonth && value.count > 0 && (
                                <Link
                                    to={`/billing/invoices?aboutMonth=${invoicesMonth}&debtorBucket=${key}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open exactly these invoices in the invoices tab"
                                    className="text-neutral-300 hover:text-primary-600">
                                    <Icon name="open_in_new" fontSize={14} />
                                </Link>
                            )}
                        </span>
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-dark">
                        {formatEuro(value.amount)}
                    </td>
                    <td className="py-1.5 pl-3 text-right tabular-nums text-neutral-500">
                        {value.count}
                    </td>
                    <td className="py-1.5 pl-3 text-right tabular-nums text-neutral-400">
                        {formatPercent1(percentageOf(value.amount, total, 1))}%
                    </td>
                </tr>
            )
        })}
    </>
)

const LegendSectionHeader = ({ label }: { label: string }) => (
    <tr>
        <td
            colSpan={4}
            className="pb-1 pt-3 text-sm font-semibold uppercase tracking-wide text-neutral-400 first:pt-0">
            {label}
        </td>
    </tr>
)

/**
 * Legend + figures for the status breakdown, grouped into open (collectable), settled (money
 * received) and closed (written off / no receivable) buckets. Percentages are relative to the
 * month total. With `invoicesMonth` set, mappable rows deep-link to the classic invoices tab.
 */
export const StatusLegend = ({
    bucket,
    invoicesMonth = null,
}: {
    bucket: DebtorBucket
    invoicesMonth?: string | null
}) => {
    const total = bucket.totals.inclVat
    const settledAmount = PAID_STATUS_KEYS.reduce(
        (sum, key) => sum + bucket.statuses[key].amount,
        0,
    )
    return (
        <table className="w-full text-md">
            <tbody>
                <LegendSectionHeader label={`Open · ${formatEuro(bucket.totals.openAmount)}`} />
                <LegendRows
                    bucket={bucket}
                    keys={OPEN_STATUS_KEYS}
                    total={total}
                    invoicesMonth={invoicesMonth}
                />
                <LegendSectionHeader label={`Settled · ${formatEuro(settledAmount)}`} />
                <LegendRows
                    bucket={bucket}
                    keys={PAID_STATUS_KEYS}
                    total={total}
                    invoicesMonth={invoicesMonth}
                />
                <LegendSectionHeader label="Closed / no receivable" />
                <LegendRows
                    bucket={bucket}
                    keys={CLOSED_STATUS_KEYS}
                    total={total}
                    invoicesMonth={invoicesMonth}
                />
            </tbody>
        </table>
    )
}

/* -------------------------------------------------------------------------- */
/*  Payment funnel                                                            */
/* -------------------------------------------------------------------------- */

interface FunnelStep {
    label: string
    help: string
    countKey:
        | 'directDebit1'
        | 'chargeback1'
        | 'chargeback1Manual'
        | 'chargeback1Other'
        | 'directDebit2'
        | 'chargeback2'
        | 'recovered'
        | 'manual'
    indent?: boolean
    tone?: 'neutral' | 'bad' | 'good'
    rateBase: FunnelStep['countKey'] | null
    rateLabel: string
}

const FUNNEL_STEPS: FunnelStep[] = [
    {
        label: '1st direct debit',
        help: 'Invoices where a first direct-debit transaction was created. Manual-transfer invoices are not part of the funnel.',
        countKey: 'directDebit1',
        rateBase: null,
        rateLabel: '',
    },
    {
        label: '↳ charged back',
        help: 'First attempt bounced (charged back or failed). Split below into debtor reversals vs. other bank reasons.',
        countKey: 'chargeback1',
        tone: 'bad',
        rateBase: 'directDebit1',
        rateLabel: 'of 1st DD',
    },
    {
        label: 'storno (debtor reversal)',
        help: 'The account holder actively reversed the direct debit (bank code MD06). Often a dispute or cash-flow issue.',
        countKey: 'chargeback1Manual',
        indent: true,
        rateBase: 'chargeback1',
        rateLabel: 'of CB1',
    },
    {
        label: 'other (insufficient funds…)',
        help: 'All other failure reasons: insufficient funds, closed or blocked account, invalid IBAN, no mandate, etc.',
        countKey: 'chargeback1Other',
        indent: true,
        rateBase: 'chargeback1',
        rateLabel: 'of CB1',
    },
    {
        label: '2nd direct debit (retry)',
        help: 'A second direct-debit attempt was created for the failed invoice.',
        countKey: 'directDebit2',
        rateBase: 'chargeback1',
        rateLabel: 'of CB1',
    },
    {
        label: '↳ charged back again',
        help: 'The retry bounced as well.',
        countKey: 'chargeback2',
        tone: 'bad',
        rateBase: 'directDebit2',
        rateLabel: 'of 2nd DD',
    },
    {
        label: '↳ recovered (paid after all)',
        help: 'Charged-back invoices that were eventually paid anyway — via the retry, a payment link, or a manual transfer.',
        countKey: 'recovered',
        tone: 'good',
        rateBase: 'chargeback1',
        rateLabel: 'of CB1',
    },
]

const funnelBarFill = (tone: FunnelStep['tone']) => {
    if (tone === 'bad') {
        return 'bg-red-400'
    }
    if (tone === 'good') {
        return 'bg-green-400'
    }
    return 'bg-primary-400'
}

export const Funnel = ({ bucket }: { bucket: DebtorBucket }) => {
    const base = bucket.metrics.directDebit1.count || 1
    return (
        <div>
            <div className="space-y-2">
                {FUNNEL_STEPS.map((step) => {
                    const value = bucket.metrics[step.countKey]
                    const width = Math.min((value.count / base) * 100, 100)
                    const rate = step.rateBase
                        ? percentageOf(value.count, bucket.metrics[step.rateBase].count)
                        : null
                    const rateColor = (() => {
                        if (step.tone === 'bad') {
                            return 'text-red-600'
                        }
                        if (step.tone === 'good') {
                            return 'text-green-600'
                        }
                        return 'text-neutral-400'
                    })()
                    return (
                        <div key={step.countKey} className={clsx('text-md', step.indent && 'pl-5')}>
                            <div className="flex items-center justify-between gap-3">
                                <HelpTip help={step.help}>
                                    <span
                                        className={clsx(
                                            step.indent ? 'text-neutral-500' : 'text-neutral-700',
                                        )}>
                                        {step.label}
                                    </span>
                                </HelpTip>
                                <span className="flex items-baseline gap-2 tabular-nums">
                                    <span className="text-dark">{value.count}</span>
                                    {rate !== null && (
                                        <span className={clsx('text-sm', rateColor)}>
                                            {rate}% {step.rateLabel}
                                        </span>
                                    )}
                                </span>
                            </div>
                            {!step.indent && (
                                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
                                    <div
                                        className={clsx(
                                            'h-full rounded-full',
                                            funnelBarFill(step.tone),
                                        )}
                                        style={{ width: `${width}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-neutral-100 pt-3 text-md">
                <HelpTip help="Invoices collected outside the direct-debit funnel: manual bank transfer, or a manual-invoice-overdue follow-up.">
                    <span className="text-neutral-700">Manual collection</span>
                </HelpTip>
                <span className="tabular-nums text-dark">{bucket.metrics.manual.count}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-md">
                <HelpTip help="Data-quality check: invoices that are not on manual transfer, never got any payment transaction or payment, and are not in debt collection or written off. Every invoice should be charged, on manual transfer, or escalated, so this should be 0.">
                    <span className="text-neutral-700">Never charged (should be 0)</span>
                </HelpTip>
                <span
                    className={clsx(
                        'tabular-nums',
                        bucket.metrics.noCharge.count > 0
                            ? 'font-semibold text-red-600'
                            : 'text-green-600',
                    )}>
                    {bucket.metrics.noCharge.count}
                </span>
            </div>
            <p className="mt-2 text-sm text-neutral-400">
                Funnel reconstructed from invoices + follow-ups; 1st/2nd split is best-effort.
            </p>
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/*  Misc badges                                                               */
/* -------------------------------------------------------------------------- */

/** Aging badge for the outstanding tab; reddens as invoices get older. */
export const AgeBadge = ({ days }: { days: number | null }) => {
    if (days === null) {
        return null
    }
    const tone = (() => {
        if (days >= 90) {
            return 'bg-red-50 text-red-700'
        }
        if (days >= 60) {
            return 'bg-amber-50 text-amber-700'
        }
        return 'bg-neutral-100 text-neutral-600'
    })()
    return (
        <HybridTooltip
            childrenContent={
                <div className="max-w-xs text-sm leading-relaxed">
                    Days since this debtor&apos;s oldest open invoice was sent (orange from 60 days,
                    red from 90).
                </div>
            }>
            <span
                className={clsx(
                    'inline-flex cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-sm font-medium',
                    tone,
                )}>
                <Icon name="schedule" fontSize={13} />
                {days}d
            </span>
        </HybridTooltip>
    )
}

const RAW_STATUS_TONES: Record<string, string> = {
    charged_back: 'bg-red-50 text-red-700',
    failed: 'bg-red-50 text-red-700',
    payment_failed: 'bg-red-50 text-red-700',
    debt_collection: 'bg-orange-50 text-orange-600',
    expired: 'bg-amber-50 text-amber-700',
    payment_reminder_overdue: 'bg-amber-50 text-amber-700',
    payment_reminder_sent: 'bg-amber-50 text-amber-700',
    payment_plan: 'bg-primary-50 text-primary-700',
    sepa_created: 'bg-primary-50 text-primary-700',
    pending: 'bg-neutral-100 text-neutral-600',
    queued: 'bg-neutral-100 text-neutral-600',
    canceled: 'bg-neutral-100 text-neutral-600',
}

/**
 * Due-date badge for an outstanding invoice row: red when overdue, gray when the invoice is not
 * due yet (e.g. the direct debit simply has not run). Renders nothing without a due date.
 */
export const DueBadge = ({ daysOverdue }: { daysOverdue: number | null }) => {
    if (daysOverdue === null) {
        return <span className="text-sm text-neutral-300">—</span>
    }
    if (daysOverdue < 0) {
        return (
            <HybridTooltip
                childrenContent={
                    <div className="max-w-xs text-sm leading-relaxed">
                        The due date is still in the future — e.g. the direct debit simply has not
                        run yet. Not a problem case.
                    </div>
                }>
                <span className="inline-flex cursor-help rounded-full bg-neutral-100 px-2 py-0.5 text-sm font-medium text-neutral-500">
                    due in {-daysOverdue}d
                </span>
            </HybridTooltip>
        )
    }
    const tone = (() => {
        if (daysOverdue >= 60) {
            return 'bg-red-50 text-red-700'
        }
        if (daysOverdue >= 14) {
            return 'bg-amber-50 text-amber-700'
        }
        return 'bg-neutral-100 text-neutral-600'
    })()
    return (
        <HybridTooltip
            childrenContent={
                <div className="max-w-xs text-sm leading-relaxed">
                    Days past the invoice due date (invoice date + payment term). Orange from 14
                    days, red from 60.
                </div>
            }>
            <span
                className={clsx(
                    'inline-flex cursor-help rounded-full px-2 py-0.5 text-sm font-medium tabular-nums',
                    tone,
                )}>
                {daysOverdue}d overdue
            </span>
        </HybridTooltip>
    )
}

/** Colored pill for a raw invoice transaction_status. */
export const StatusPill = ({ status }: { status: string }) => {
    const tone = RAW_STATUS_TONES[status] || 'bg-neutral-100 text-neutral-600'
    const label = status ? status.replace(/_/g, ' ') : 'no status'
    return (
        <span className={clsx('inline-block rounded-full px-2 py-0.5 text-sm font-medium', tone)}>
            {label}
        </span>
    )
}
